import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { ArrowLeft, Cookie, Shield, Lock } from 'lucide-react';

const CookiesPolicy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        
        <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-full">
              <Cookie className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-heading font-extrabold text-foreground">{t('cookiesPolicy.title')}</h1>
              <p className="text-muted-foreground">{t('cookiesPolicy.subtitle')}</p>
            </div>
          </div>
          
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="font-semibold text-foreground">{t('cookiesPolicy.controller')}:</p>
              <p>2DS Finland Oy</p>
              <p>Business ID: 3246359-5</p>
              <p>Espoo, Finland</p>
              <p>Email: sales@2ds.fi</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="font-semibold text-blue-800">{t('cookiesPolicy.lastUpdated')}: December 2025</p>
            </div>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">1</span>
                {t('cookiesPolicy.whatAreCookies')}
              </h2>
              <p>{t('cookiesPolicy.whatAreCookiesDesc')}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">2</span>
                {t('cookiesPolicy.howWeUseCookies')}
              </h2>
              <p>{t('cookiesPolicy.howWeUseCookiesDesc')}</p>
              
              <div className="mt-4 space-y-4">
                <div className="border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-foreground">{t('cookiesPolicy.essentialCookies')}</h3>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">{t('gdpr.required')}</span>
                  </div>
                  <p className="text-sm">{t('cookiesPolicy.essentialCookiesDesc')}</p>
                  <table className="w-full mt-3 text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">{t('cookiesPolicy.cookieName')}</th>
                        <th className="text-left py-2">{t('cookiesPolicy.purpose')}</th>
                        <th className="text-left py-2">{t('cookiesPolicy.duration')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">auth_token</td>
                        <td className="py-2">{t('cookiesPolicy.authTokenPurpose')}</td>
                        <td className="py-2">{t('cookiesPolicy.sessionDuration')}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">privacy_consent</td>
                        <td className="py-2">{t('cookiesPolicy.consentPurpose')}</td>
                        <td className="py-2">{t('cookiesPolicy.sessionDuration')}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 font-mono text-xs">i18nextLng</td>
                        <td className="py-2">{t('cookiesPolicy.languagePurpose')}</td>
                        <td className="py-2">{t('cookiesPolicy.persistentDuration')}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">3</span>
                {t('cookiesPolicy.cookiesWeDoNotUse')}
              </h2>
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <p className="text-green-800 font-medium mb-2">{t('cookiesPolicy.weDoNotUse')}:</p>
                <ul className="list-disc pl-6 space-y-1 text-green-700">
                  <li>{t('cookiesPolicy.noAdvertising')}</li>
                  <li>{t('cookiesPolicy.noThirdPartyTracking')}</li>
                  <li>{t('cookiesPolicy.noAnalytics')}</li>
                  <li>{t('cookiesPolicy.noSocialMedia')}</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">4</span>
                {t('cookiesPolicy.yourChoices')}
              </h2>
              <p>{t('cookiesPolicy.yourChoicesDesc')}</p>
              <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm">
                  <strong>{t('cookiesPolicy.note')}:</strong> {t('cookiesPolicy.disablingNote')}
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">5</span>
                {t('cookiesPolicy.legalBasis')}
              </h2>
              <p>{t('cookiesPolicy.legalBasisDesc')}</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong>{t('cookiesPolicy.legitimateInterest')}:</strong> {t('cookiesPolicy.legitimateInterestDesc')}</li>
                <li><strong>{t('cookiesPolicy.contractualNecessity')}:</strong> {t('cookiesPolicy.contractualNecessityDesc')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">6</span>
                {t('cookiesPolicy.dataRetention')}
              </h2>
              <p>{t('cookiesPolicy.dataRetentionDesc')}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">7</span>
                {t('cookiesPolicy.yourRights')}
              </h2>
              <p>{t('cookiesPolicy.yourRightsDesc')}</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>{t('cookiesPolicy.rightAccess')}</li>
                <li>{t('cookiesPolicy.rightRectification')}</li>
                <li>{t('cookiesPolicy.rightErasure')}</li>
                <li>{t('cookiesPolicy.rightRestriction')}</li>
                <li>{t('cookiesPolicy.rightPortability')}</li>
                <li>{t('cookiesPolicy.rightObjection')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">8</span>
                {t('cookiesPolicy.contact')}
              </h2>
              <p>{t('cookiesPolicy.contactDesc')}</p>
              <div className="mt-3 p-4 bg-muted/50 rounded-lg">
                <p><strong>2DS Finland Oy</strong></p>
                <p>Email: sales@2ds.fi</p>
                <p>Espoo, Finland</p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mt-6 mb-3 flex items-center gap-2">
                <span className="bg-primary/10 p-1 rounded">9</span>
                {t('cookiesPolicy.supervisoryAuthority')}
              </h2>
              <p>{t('cookiesPolicy.supervisoryAuthorityDesc')}</p>
              <div className="mt-3 p-4 bg-muted/50 rounded-lg">
                <p><strong>{t('cookiesPolicy.finnishDPA')}</strong></p>
                <p>Lintulahdenkuja 4, 00530 Helsinki</p>
                <p>tietosuoja@om.fi</p>
              </div>
            </section>

            <section className="border-t border-border pt-6 mt-6">
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5" />
                {t('cookiesPolicy.policyUpdates')}
              </h2>
              <p>{t('cookiesPolicy.policyUpdatesDesc')}</p>
            </section>
          </div>

          <div className="mt-8 flex gap-4">
            <Button onClick={() => navigate('/privacy-policy')} variant="outline">
              {t('footer.privacyPolicy')}
            </Button>
            <Button onClick={() => navigate('/terms-of-service')} variant="outline">
              {t('footer.termsOfService')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicy;
